import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Handshake, Building2, Car, Users, Star, ArrowRight, CheckCircle, Gift } from "lucide-react";
import Marquee from "react-fast-marquee";

const partnerTiers = [
  {
    name: "Visionary Partner",
    amount: "$10,000",
    description: "Transform entire communities with your visionary support",
    benefits: [
      "Premier logo placement on all materials",
      "Named program sponsorship opportunity",
      "VIP access to all SOAR events",
      "Quarterly impact reports",
      "Feature in annual report",
      "Recognition at major events"
    ],
    accent: true,
    icon: Star
  },
  {
    name: "Empower Partner",
    amount: "$5,000",
    description: "Empower youth to build their dreams and futures",
    benefits: [
      "Logo placement on materials",
      "Event sponsorship recognition",
      "Access to SOAR events",
      "Bi-annual impact reports",
      "Social media recognition"
    ],
    icon: Users
  },
  {
    name: "Impact Partner",
    amount: "$1,000",
    description: "Create direct impact in the lives of young people",
    benefits: [
      "Website recognition",
      "Event invitation privileges",
      "Annual impact report",
      "Newsletter features"
    ],
    icon: Heart
  },
  {
    name: "Think For Self",
    amount: "Any Amount",
    description: "Every contribution helps shape a young person's future",
    benefits: [
      "Join our community of supporters",
      "Receive program updates",
      "Know your gift makes a difference"
    ],
    icon: Gift
  }
];

const Partners = () => {
  return (
    <div data-testid="partners-page" className="bg-white">

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?w=1600"
            alt="Partners"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/95 to-soar-blue/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
              Partnership Opportunities
            </span>
            <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight text-white opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              How You Can <span className="text-soar-gold">Help</span>
            </h1>
            <p className="text-xl text-white/80 leading-relaxed opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              Our job is to teach what has not been taught.
              If we haven't figured out how to make it easier for you to grow as a person, then we're
              probably not finished yet.
            </p>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-5" style={{ backgroundColor: '#212163' }}>
        <Marquee speed={50} gradient={false}>
          {["PARTNER", "SUPPORT", "TRANSFORM", "EMPOWER", "INSPIRE", "GIVE"].map((w) => (
            <span key={w} className="font-heading text-4xl mx-12 tracking-wider" style={{ color: 'rgba(221,182,77,0.3)' }}>{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Mission Statement */}
      <section className="py-24 bg-white" data-testid="mission-statement">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-accent text-4xl md:text-5xl text-soar-gold mb-6">
            "From your heart to their future"
          </p>
          <p className="text-lg text-soar-blue/50 leading-relaxed">
            Your donation supports youth confidence, skills, and self-worth. Together, we're building
            a generation of leaders who think critically, speak confidently, and take ownership of their futures.
          </p>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section className="py-24 bg-white" data-testid="partnership-tiers">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-soar-teal text-xs font-semibold tracking-widest uppercase">Make an Impact</span>
            <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight mt-3">
              Partnership Levels
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerTiers.map((tier) => (
              <div
                key={tier.name}
                className={`card-elevated rounded-3xl p-6 ${
                  tier.accent ? 'ring-2 ring-soar-gold' : ''
                }`}
              >
                <div className={`w-16 h-16 rounded-2xl mb-4 flex items-center justify-center ${
                  tier.accent ? 'bg-soar-gold/20' : 'bg-soar-teal/15'
                }`}>
                  <tier.icon className={`w-8 h-8 ${tier.accent ? 'text-soar-gold' : 'text-soar-teal'}`} aria-hidden="true" />
                </div>

                {tier.accent && (
                  <div className="inline-block bg-soar-gold text-[#0a0a2e] text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 uppercase tracking-wider">
                    Most Impact
                  </div>
                )}

                <h3 className="font-heading text-xl text-soar-blue mb-1 tracking-tight">{tier.name}</h3>
                <p className="font-heading text-3xl text-soar-teal mb-2 tracking-tight">{tier.amount}</p>
                <p className="text-soar-blue/50 text-sm mb-4">{tier.description}</p>

                <ul className="space-y-2 mb-6">
                  {tier.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-soar-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-soar-blue/50">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/donate">
                  <Button className={`w-full rounded-full font-bold hover:scale-[1.02] transition-transform duration-200 ${
                    tier.accent
                      ? 'bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e]'
                      : 'bg-soar-teal hover:bg-soar-teal/90 text-white'
                  }`}>
                    Partner Now
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Campaigns */}
      <section className="py-24 bg-white" data-testid="special-campaigns">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">Campaigns</span>
            <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight mt-3">
              Special Campaigns
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-56 bg-soar-teal flex items-center justify-center">
                <Car className="w-24 h-24 text-white/10" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <h3 className="font-heading text-3xl tracking-tight">Vehicle Campaign</h3>
                  <p className="text-white/70 text-sm mt-1">Help us get transportation to serve more youth</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-soar-blue/50 text-sm mb-4">
                  A vehicle will allow us to transport youth to events, college visits,
                  community service projects, and more.
                </p>
                <Link to="/donate">
                  <Button className="w-full rounded-full bg-soar-teal hover:bg-soar-teal/90 font-bold hover:scale-[1.02] transition-transform duration-200">
                    Contribute <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-56 bg-soar-gold flex items-center justify-center">
                <Building2 className="w-24 h-24 text-[#0a0a2e]/10" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <h3 className="font-heading text-3xl tracking-tight">Building Campaign</h3>
                  <p className="text-white/70 text-sm mt-1">Help us secure a permanent home for SOAR</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-soar-blue/50 text-sm mb-4">
                  A dedicated space will give us room to host workshops, mentorship sessions,
                  and provide a safe space for youth.
                </p>
                <Link to="/donate">
                  <Button className="w-full rounded-full bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e] font-bold hover:scale-[1.02] transition-transform duration-200">
                    Contribute <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Partner */}
      <section className="py-24 bg-white" data-testid="ways-to-partner">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-4xl md:text-5xl text-soar-blue tracking-tight">
              Other Ways to Partner
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card-elevated rounded-3xl p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-soar-teal/15 flex items-center justify-center group-hover:bg-soar-teal/25 transition-colors">
                <Users className="w-8 h-8 text-soar-teal" aria-hidden="true" />
              </div>
              <h3 className="font-heading text-xl text-soar-blue mb-2 tracking-tight">Volunteer Your Time</h3>
              <p className="text-soar-blue/50 text-sm mb-4">Share your skills and experience as a mentor or event volunteer.</p>
              <Link to="/volunteer-form">
                <Button className="rounded-full bg-soar-blue/10 hover:bg-soar-blue/15 border border-soar-blue/20 text-soar-blue font-bold hover:scale-[1.02] transition-transform duration-200">
                  Volunteer
                </Button>
              </Link>
            </div>
            <div className="card-elevated rounded-3xl p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-soar-gold/15 flex items-center justify-center group-hover:bg-soar-gold/25 transition-colors">
                <Handshake className="w-8 h-8 text-soar-gold" aria-hidden="true" />
              </div>
              <h3 className="font-heading text-xl text-soar-blue mb-2 tracking-tight">Corporate Partnership</h3>
              <p className="text-soar-blue/50 text-sm mb-4">Partner with us for events like Fresh Start or Feed the Deserved.</p>
              <Link to="/contact">
                <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e] font-bold hover:scale-[1.02] transition-transform duration-200">
                  Contact Us
                </Button>
              </Link>
            </div>
            <div className="card-elevated rounded-3xl p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-soar-blue/10 flex items-center justify-center group-hover:bg-soar-blue/15 transition-colors">
                <Heart className="w-8 h-8 text-soar-blue/80" aria-hidden="true" />
              </div>
              <h3 className="font-heading text-xl text-soar-blue mb-2 tracking-tight">In-Kind Donations</h3>
              <p className="text-soar-blue/50 text-sm mb-4">Donate supplies, services, or resources to support our programs.</p>
              <Link to="/contact">
                <Button className="rounded-full bg-soar-blue/10 hover:bg-soar-blue/15 border border-soar-blue/20 text-soar-blue font-bold hover:scale-[1.02] transition-transform duration-200">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Current Partners */}
      <section className="py-24 bg-white" data-testid="current-partners">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-soar-teal text-xs font-semibold tracking-widest uppercase">Our Community</span>
            <h2 className="font-heading text-4xl md:text-5xl text-soar-blue mt-3 tracking-tight">Proud Partners</h2>
            <p className="text-soar-blue/50 mt-4 max-w-2xl mx-auto">
              We are grateful for the organizations and families who believe in our mission and help us empower youth.
            </p>
          </div>

          {/* Corporate & Organization Partners */}
          <div className="mb-16">
            <h3 className="text-center text-soar-gold text-sm font-semibold tracking-widest uppercase mb-8">Organizations & Businesses</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/ee9b5la7_macys.jpg", alt: "Macy's" },
                { src: "https://customer-assets.emergentagent.com/job_youth-impact-5/artifacts/7pg0scvy_Pepsi.jpg", alt: "Pepsi" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/7ybpfe12_KCF.png", alt: "Kern Community Foundation" },
                { src: "https://customer-assets.emergentagent.com/job_youth-impact-5/artifacts/mamih8jm_california%20fish%20grill.png", alt: "California Fish Grill" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/0oxh3fuo_upside.png", alt: "Upside Academy, Inc." },
                { src: "https://customer-assets.emergentagent.com/job_youth-impact-5/artifacts/ahfc439t_Logo-Full.png", alt: "Cognitive Center" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/wssxwjtv_cardiovascular.png", alt: "Comprehensive Cardiovascular" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/z0l4s3j3_1.Forge%20103.9.png", alt: "Forge 103.9" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/i2krszod_IMG_0372.jpg", alt: "Oh So Crafty" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/w20n9j8g_sysy.png", alt: "Sy Sy's Snacks" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/fzp402a5_Screenshot%202025-11-19%20153406.png", alt: "That's SEW You" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/0ozcwmy5_image000000.jpg", alt: "Uncle Swee Ps" },
              ].map((partner) => (
                <div key={partner.alt} className="card-elevated rounded-2xl p-4 flex items-center justify-center h-24 group hover:border-soar-teal/30 transition-all">
                  <img src={partner.src} alt={partner.alt} className="max-h-14 max-w-full object-contain opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          </div>

          {/* Community Partners */}
          <div className="mb-16">
            <h3 className="text-center text-soar-gold text-sm font-semibold tracking-widest uppercase mb-8">Community Partners</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[
                { src: "https://customer-assets.emergentagent.com/job_youth-impact-5/artifacts/ckbpmctk_Dream%20Center%20Logo.jpg", alt: "The Dream Center" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/vd6c5958_Jamison%20Center.png", alt: "Jamison Children's Center" },
                { src: "https://customer-assets.emergentagent.com/job_youth-impact-5/artifacts/ryo6oczi_Moms-House.jpg", alt: "Mom's House" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/8oeyar44_3.jpg", alt: "House of Ruth Outreach Ministry" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/k23itjqy_IMG_7878.jpg", alt: "N|L Church" },
                { src: "https://customer-assets.emergentagent.com/job_youth-impact-5/artifacts/46rbuyae_IMG_8731.jpg", alt: "TYM4C" },
                { src: "https://customer-assets.emergentagent.com/job_youth-impact-5/artifacts/rmwr9rtl_driller%20way.jpg", alt: "The Driller Way" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/wmfu1vb2_Warm%20Gold%20Cross%20with%20Embracing%20Hands%20Logo%20%281%29.png", alt: "Anthem" },
              ].map((partner) => (
                <div key={partner.alt} className="card-elevated rounded-2xl p-4 flex items-center justify-center h-24 group hover:border-soar-teal/30 transition-all">
                  <img src={partner.src} alt={partner.alt} className="max-h-14 max-w-full object-contain opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          </div>

          {/* Family Sponsors */}
          <div>
            <h3 className="text-center text-soar-gold text-sm font-semibold tracking-widest uppercase mb-8">Family Sponsors</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/p15m7l3y_Grey%20Minimalist%20Elegant%20Signature%20Logo%20-%201.png", alt: "Jennings Family" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/n2o00bv6_Black%20%26%20White%20Minimalist%20Signature%20Personal%20Brand%20Logo%20-%201.png", alt: "Pulido Family" },
                { src: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/6g7gk1d8_Black%20White%20Elegant%20Monogram%20Initial%20Name%20Logo%20-%201.png", alt: "Stewart Family" },
              ].map((family) => (
                <div key={family.alt} className="card-elevated rounded-2xl p-6 flex flex-col items-center justify-center group hover:border-soar-gold/30 transition-all">
                  <img src={family.src} alt={family.alt} className="h-20 object-contain opacity-80 group-hover:opacity-100 transition-opacity" />
                  <p className="text-soar-blue/60 text-xs mt-3 font-medium">{family.alt}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-soar-blue/50 mt-16 text-center text-sm">
            Interested in becoming a partner? <Link to="/contact" className="text-soar-teal hover:underline">Contact us</Link> to learn more.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative overflow-hidden" data-testid="partners-cta-section">
        <div className="absolute inset-0 bg-soar-gold" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-5xl md:text-6xl text-[#0a0a2e] tracking-tight mb-6">Ready to Make a Difference?</h2>
          <p className="text-[#0a0a2e]/60 mb-10 max-w-2xl mx-auto text-lg">
            Your support helps us empower youth to think, build, and dream. Together,
            we can create successful outcomes and results that change lives.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate" data-testid="partners-donate-btn">
              <Button className="rounded-full bg-white hover:bg-white/90 text-soar-blue px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                <Heart className="w-5 h-5 mr-2" /> Donate Now
              </Button>
            </Link>
            <Link to="/contact" data-testid="partners-contact-btn">
              <Button className="rounded-full bg-soar-blue/20 hover:bg-white/30 text-[#0a0a2e] border border-[#0a0a2e]/20 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
